import IdleJs from 'idle-js';
import { v4 as uuidv4 } from 'uuid';
import { MILLISECONDS_PER_HOUR, millisecondsPerMinute } from '../constants';
import { SessionRecorder } from './sessionRecorder';
import { getNowTime, getSdkConfig, getSessionRecorder, getSnapshotManager, } from '../helpers';
import { PREV_SESSION_KEY, SESSION_EXPIRATION_IN_HOURS, SESSION_IDLE_TIME_IN_MINUTES, SESSION_KEY, SESSION_MANAGER_KEY, } from './session.consts';
import { isSamplingOn } from './session.helper';
export class SessionManager {
    constructor(recordConfig) {
        this._sessionConfig = getSdkConfig().sessionConfig;
        this._isIdleActive = false;
        this._sessionHasError = false;
        this._cachedLogsSent = false;
        this.getPrevSession = () => {
            return JSON.parse(window.sessionStorage.getItem(PREV_SESSION_KEY));
        };
        this.getSession = () => {
            const storedSession = JSON.parse(window.sessionStorage.getItem(SESSION_KEY));
            const now = getNowTime();
            let session;
            if (storedSession) {
                if (storedSession.sessionExpirationDate > now) {
                    session = storedSession;
                }
                else {
                    this.clearSessionWhenIdle();
                    session = this.setSession(true);
                }
            }
            return session;
        };
        this.setSession = (afterIdle = false, sessionId) => {
            var _a, _b;
            const now = getNowTime();
            const expirationDate = now + MILLISECONDS_PER_HOUR * SESSION_EXPIRATION_IN_HOURS;
            const session = {
                sessionId: sessionId || uuidv4(),
                sessionCreationDate: now,
                sessionExpirationDate: expirationDate,
            };
            this.activeSession = session;
            window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
            const sessionRecorder = getSessionRecorder();
            const shouldStartRecordingAfterIdle = afterIdle &&
                !!(sessionRecorder === null || sessionRecorder === void 0 ? void 0 : sessionRecorder.recordingStopDueToTimeout) &&
                !!((_a = this.sessionRecorderConfig) === null || _a === void 0 ? void 0 : _a.enable);
            if (shouldStartRecordingAfterIdle ||
                (sessionRecorder === null || sessionRecorder === void 0 ? void 0 : sessionRecorder.getIsAutoStartRecording())) {
                (_b = this.sessionRecorder) === null || _b === void 0 ? void 0 : _b.startRecording();
            }
            return session;
        };
        this.initIdle();
        window[SESSION_MANAGER_KEY] = this;
        this.sessionRecorderConfig = recordConfig;
        if (recordConfig === null || recordConfig === void 0 ? void 0 : recordConfig.enable) {
            const recordingSamplingEnabled = isSamplingOn(recordConfig.sessionRecordingSampleRate);
            if (recordingSamplingEnabled) {
                this.sessionRecorder = new SessionRecorder(this, recordConfig);
            }
            else {
                if (getSdkConfig().debug) {
                    console.debug('Coralogix Browser SDK - Session recording is disabled');
                }
            }
        }
    }
    get currentPageFragment() {
        return this._currentPageFragment;
    }
    set currentPageFragment(value) {
        this._currentPageFragment = value;
    }
    get isIdleActive() {
        return this._isIdleActive;
    }
    get sessionConfig() {
        return this._sessionConfig;
    }
    get onlySessionWithErrorMode() {
        return this._sessionConfig.onlyWithErrorConfig.enable;
    }
    get maxRumEventsForSessionWithError() {
        return this._sessionConfig.onlyWithErrorConfig.maxRumEvents || 0;
    }
    get maxRecordTimeForSessionWithError() {
        return this._sessionConfig.onlyWithErrorConfig.maxRecordTime || 0;
    }
    get instrumentationsToSend() {
        return this._sessionConfig.onlyWithErrorConfig.instrumentationsToSend;
    }
    get cachedLogsSent() {
        return this._cachedLogsSent;
    }
    get sessionHasError() {
        return this._sessionHasError;
    }
    set sessionHasError(value) {
        this._sessionHasError = value;
    }
    set cachedLogsSent(val) {
        this._cachedLogsSent = val;
    }
    getSessionKey() {
        return SESSION_KEY;
    }
    getActiveSession() {
        return this.activeSession;
    }
    start() {
        this.idle.start();
        this.clearPrevSession();
        this.setSession();
    }
    stop() {
        this.idle.stop();
    }
    clearSessionWhenIdle() {
        var _a;
        if ((_a = this.sessionRecorder) === null || _a === void 0 ? void 0 : _a.getSessionHasRecording()) {
            this.sessionRecorder.recordingStopDueToTimeout = true;
        }
        this.clearSession();
    }
    clearPrevSession() {
        window.sessionStorage.removeItem(PREV_SESSION_KEY);
    }
    initIdle() {
        if (this.idle) {
            return;
        }
        this.idle = new IdleJs({
            idle: SESSION_IDLE_TIME_IN_MINUTES * millisecondsPerMinute,
            events: ['mousemove', 'keydown', 'touchstart'],
            onIdle: () => {
                this.clearSessionWhenIdle();
                this._isIdleActive = true;
            },
            onActive: () => {
                this.setSession(true);
                this._isIdleActive = false;
            },
            // onHide: () => {}, // callback function to be executed when window become hidden
            // onShow: () => {}, // callback function to be executed when window become visible
            keepTracking: true,
            startAtIdle: false, // set it to true if you want to start in the idle state
        });
    }
    clearSession() {
        var _a, _b, _c;
        window.sessionStorage.setItem(PREV_SESSION_KEY, JSON.stringify(Object.assign(Object.assign({}, this.activeSession), { hasRecording: (_a = this.sessionRecorder) === null || _a === void 0 ? void 0 : _a.getSessionHasRecording() })));
        this.clearSessionWithErrorMode();
        (_b = getSnapshotManager()) === null || _b === void 0 ? void 0 : _b.resetSnapshot();
        this._currentPageFragment = undefined;
        (_c = this.sessionRecorder) === null || _c === void 0 ? void 0 : _c.stopRecording();
        window.sessionStorage.removeItem(SESSION_KEY);
    }
    clearSessionWithErrorMode() {
        this.sessionHasError = false;
        this.cachedLogsSent = false;
    }
}
//# sourceMappingURL=sessionManager.js.map